import {
  CancelToken as axiosCancelToken,
  isCancel as isAxiosCancel
} from "axios";
import {
  cloneDeep as _cloneDeep,
  has as _has
} from "lodash";
import { AxiosLegislatorResults } from "Api/public/axios";
import {isEmpty as _isEmpty} from "lodash";
import {mapActions} from "vuex";
import * as ACTIONS from "Constants/campaign/actionTypes";

export default {
  inject: {
    axiosHelper: { from: "axiosHelper" }
  },
  computed: {
    cancelTokenSource() {
      return this.cancelSources.legislatorResults;
    },
    currentLegislatorMessage() {
      const key = Object.keys(this.legislatorMessages)[this.currentMessage];
      return this.legislatorMessages[key] || null;
    },
    legislatorMessages() {
      return this.legislatorResults[this.currentAction] || [];
    },
  },
  methods: {
    ...mapActions({
      setFormError: "forms/error",
      setCancelSource: "api/cancelSources",
      setLegislatorsErrorMessage: "legislators/errorMessage",
      setShowOneStepClosing: "legislators/showOneStepClosing"
    }),
    cancelPreviousGeoSearchRequest() {
      // cancel the previous request
      // https://github.com/axios/axios#cancellation
      if (this.shouldCancelPreviousGeoSearchRequest()) {
        this.cancelTokenSource.cancel("Cancelled previous request");
      }
      // save the new request for cancellation
      this.setCancelSource({
        apiName: "legislatorResults",
        value: axiosCancelToken.source()
      });
    },
    shouldCancelPreviousGeoSearchRequest() {
      return (
        !_isEmpty(this.cancelTokenSource) &&
        typeof this.cancelTokenSource.cancel === "function"
      );
    },
    async geosearchLegislators(address = {}) {
      if (_isEmpty(address)) {
        return {};
      }

      this.$store.dispatch(`legislators/loading`, true);
      this.$store.dispatch("navigation/actionLoading", true);

      try {
        this.cancelPreviousGeoSearchRequest();
        const response = await this.fetchLegislatorsAndMessages(address);
        this.$store.dispatch(
          `legislators/fetchLegislatorsAddressPayload`,
          address
        );
        const legislatorResults = response.data;
        this.handleAfterGeosearchLegislators(legislatorResults);
        return legislatorResults;
      } catch (error) {
        if (isAxiosCancel(error)) {
          // previous requests cancelled do not put the component
          // in an error state. Do not treat it as error
          return {};
        }
        const errorMessage = this.axiosHelper.retrieveErrorMessage(error);
        this.$store.dispatch("navigation/actionLoading", false);

        this.setFormError(errorMessage);

        if (!this.showOneStep) {
          this.setLegislatorsErrorMessage(errorMessage);
          this.showErrorModal();
        }

        this.$store.dispatch(`legislators/resetLegislatorResults`);

        this.$store.dispatch(
          `legislators/fetchLegislatorsAddressPayload`,
          address
        );

        this.$store.dispatch(`legislators/loading`, false);
        return errorMessage;
      }
    },
    handleAfterGeosearchLegislators(legislatorResults) {
      this.setApiLegislatorResults(legislatorResults);

      this.$store.dispatch("navigation/campaignRestrictionsError", false);

      if (legislatorResults[this.currentAction].length < 1) {
        // no legislators found, display error message
        if (this.showOneStep) {
          this.setFormError(this.noLegislatorsFoundErrorMessage);
        } else {
          this.setLegislatorsErrorMessage(this.noLegislatorsFoundErrorMessage);
          this.showErrorModal();
        }

        this.$store.dispatch(`legislators/resetLegislatorResults`);

        this.$store.dispatch(`legislators/loading`, false);
        this.$store.dispatch("navigation/actionLoading", false);
        return;
      }

      if (this.showOneStep) {
        this.setShowOneStepClosing(true);
      }

      const preservedLegislatorResults = this.preserveChangedEmailMessage(
        this.legislatorResults,
        legislatorResults
      );

      this.$store.dispatch(
        "legislators/legislatorResults",
        preservedLegislatorResults
      );
      this.$store.dispatch(`legislators/loading`, false);
      this.$store.dispatch("navigation/actionLoading", false);
      this.setLegislatorsErrorMessage("");
      this.setFormError("");
    },
    setApiLegislatorResults(legislatorResults) {
      this.$store.dispatch(
        `legislators/apiLegislatorResults`,
        _cloneDeep(legislatorResults)
      );
    },
    preserveChangedEmailMessage(oldLegislatorResults, newLegislatorResults) {
      const updatedLegislatorResults = newLegislatorResults;

      if (
        _has(oldLegislatorResults, "email") &&
        oldLegislatorResults.email.length === 1 &&
        _has(oldLegislatorResults.email[0], "message") &&
        oldLegislatorResults.email[0].message.id ===
        newLegislatorResults.email[0].message.id
      ) {
        const oldMessage = oldLegislatorResults.email[0].message;

        updatedLegislatorResults.email[0].message = oldMessage;
      }

      return updatedLegislatorResults;
    },
    fetchLegislatorsAndMessages(address = {}) {
      const messageid = this.getEmailMessageId();

      const honeypotZipcode = _isEmpty(address.zipcode)
        ? this.loadStart
        : address.zipcode;

      let geoSearchParameters = {
        street: address.address1,
        zip: address.zip5,
        messageid,
        // honeypot
        zipcode: honeypotZipcode,
        address: address.address,
        state: address.state,
        city: address.city,
      };

      if (address.lat && address.lon) {
        geoSearchParameters.lat = address.lat;
        geoSearchParameters.lon = address.lon;
      }

      if (!_isEmpty(address.county)) {
        geoSearchParameters.county = address.county;
      }

      if (!_isEmpty(address.country)) {
        geoSearchParameters = {
          ...geoSearchParameters,
          country: address.country
        };
      }

      return AxiosLegislatorResults.get("", {
        params: geoSearchParameters,
        cancelToken: this.cancelTokenSource.token
      });
    },
    getEmailMessageId() {
      if (this.hasOneStep) {
        return this.currentAction === ACTIONS.EMAIL
          ? this.currentLegislatorMessage.message.id
          : this.legislatorResults.email[0].message.id;
      }

      return null;
    },
  }
}
